import React, { useCallback, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  BackgroundVariant,
  NodeTypes,
  EdgeTypes,
  ReactFlowInstance,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './WorkflowBuilder.css';
import { marked } from 'marked';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Avatar,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import variables from '../../../assets/styles/variables.module.scss';
import WorkflowService, { SaveWorkflowRequest, LoadWorkflowResponse } from '../../../Services/WorkflowService';
import BaseInstance from '../../../Services/AxiosInstance/BaseInstance';

// Import custom node components
import UserInputNode from './components/UserInputNode';
import SupervisorNode from './components/SupervisorNode';
import AgentNode from './components/AgentNode';
import KnowledgeBaseNode from './components/KnowledgeBaseNode';
import DataParserNode from './components/DataParserNode';
import OutputNode from './components/OutputNode';
import ConditionalRouteNode from './components/ConditionalRouteNode';
import PDFGeneratorNode from './components/PDFGeneratorNode';
import EmailSenderNode from './components/EmailSenderNode';
import ConditionalRouteConfigPanel from './components/ConditionalRouteConfigPanel';
import PDFGeneratorConfigPanel from './components/PDFGeneratorConfigPanel';
import EmailSenderConfigPanel from './components/EmailSenderConfigPanel';
import AnimatedDashedEdge from './components/AnimatedDashedEdge';
import SupervisorConfigPanel from './components/SupervisorConfigPanel';
import AgentConfigPanel from './components/AgentConfigPanel';
import KnowledgeBaseConfigPanel from './components/KnowledgeBaseConfigPanel';
import DataParserConfigPanel from './components/DataParserConfigPanel';
import { useDispatch } from 'react-redux';
import { StartLoading, StopLoading } from '../../../Shared-ui/Store/LoadingReducer/LoaderActions';
import { ShowError } from '../../../Shared-ui/Store/MessageReducer/MsgActions';
import AgentPrompts from '../../Components/AgentPromts';
import { Message } from '../../../Shared-ui/Types/PlayGround';
import { PlayGroundEnum } from '../../../Shared-ui/Enum/PlayGroundEnum';

// Configure marked to be synchronous
marked.setOptions({
  async: false,
});

// Helper function to safely parse markdown
const parseMarkdown = (content: string): string => {
  try {
    return marked.parse(content) as string;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return content; // Return original content if parsing fails
  }
};

// Define node types
const nodeTypes: NodeTypes = {
  userInput: UserInputNode,
  supervisor: SupervisorNode,
  agent: AgentNode,
  knowledgeBase: KnowledgeBaseNode,
  dataParser: DataParserNode,
  output: OutputNode,
  conditionalRoute: ConditionalRouteNode,
  pdfGenerator: PDFGeneratorNode,
  emailSender: EmailSenderNode,
};

// Define edge types
const edgeTypes: EdgeTypes = {
  animatedDashed: AnimatedDashedEdge,
};

// Initial nodes
const initialNodes: Node[] = [
  {
    id: '1',
    type: 'userInput',
    position: { x: 250, y: 50 },
    data: { label: 'User Input', inputType: 'Input Message' },
  },
];

// Initial edges
const initialEdges: Edge[] = [];

const WorkflowBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId?: string }>();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const dispatch = useDispatch();

  // State declarations
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewWorkflow, setIsNewWorkflow] = useState(true);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [workflowConversationId, setWorkflowConversationId] = useState<string | undefined>(undefined);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Custom edge change handler to detect deletions
  const handleEdgesChange = useCallback((changes: any[]) => {
    onEdgesChange(changes);

    // Check for edge deletions
    changes.forEach((change) => {
      if (change.type === 'remove') {
        const removedEdge = edges.find(edge => edge.id === change.id);
        if (removedEdge && removedEdge.sourceHandle === 'agents') {
          // Show agent's output handle when disconnected from supervisor
          setNodes((nds) => nds.map((node) => {
            if (node.id === removedEdge.target && node.type === 'agent') {
              return {
                ...node,
                data: {
                  ...node.data,
                  hideOutputHandle: false,
                },
              };
            }
            return node;
          }));
        }
        if (removedEdge && removedEdge.sourceHandle === 'false') {
          // Handle false connection deletion
          console.log('False connection deleted:', removedEdge);
          setNodes((nds) => nds.map((node) => {
            if (node.id === removedEdge.source && node.type === 'conditionalRoute') {
              const currentFalseConnections = node.data.falseConnections || [];
              const updatedFalseConnections = currentFalseConnections.filter(
                (targetId: string) => targetId !== removedEdge.target
              );
              return {
                ...node,
                data: {
                  ...node.data,
                  falseConnections: updatedFalseConnections,
                },
              };
            }
            return node;
          }));
        }
      }
    });
  }, [edges, onEdgesChange, setNodes]);

  // Custom node change handler to detect deletions and close config panel
  const handleNodesChange = useCallback((changes: any[]) => {
    onNodesChange(changes);

    // Check for node deletions
    changes.forEach((change) => {
      if (change.type === 'remove' && selectedNode && change.id === selectedNode.id) {
        // Close config panel if the deleted node is currently selected
        setShowConfigPanel(false);
        setSelectedNode(null);
      }
    });
  }, [onNodesChange, selectedNode]);

  // Load workflow if editing existing one
  useEffect(() => {
    if (workflowId && workflowId !== 'create') {
      loadWorkflow(workflowId);
      setIsNewWorkflow(false);
    }
  }, [workflowId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, isExecuting]);

  // Check existing connections to hide agent output handles
  useEffect(() => {
    console.log('Checking existing connections. Edges:', edges); // Debug log
    setNodes((nds) => nds.map((node) => {
      if (node.type === 'agent') {
        const isConnectedToSupervisor = edges.some(edge =>
          edge.target === node.id && edge.sourceHandle === 'agents'
        );
        console.log(`Agent ${node.id} connected to supervisor:`, isConnectedToSupervisor); // Debug log
        return {
          ...node,
          data: {
            ...node.data,
            hideOutputHandle: isConnectedToSupervisor,
          },
        };
      }
      return node;
    }));
  }, [edges, setNodes]);

  const loadWorkflow = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const workflowData = await WorkflowService.loadWorkflow(id);
      console.log('loadWorkflow - Received Data:', workflowData);

      setWorkflowName(workflowData.name);
      setWorkflowDescription(workflowData.description || '');

      // Convert API format to React Flow format
      const { nodes: reactFlowNodes, edges: reactFlowEdges } = WorkflowService.convertToReactFlowFormat(
        workflowData.nodes,
        workflowData.edges
      );

      setNodes(reactFlowNodes);
      setEdges(reactFlowEdges);
    } catch (err) {
      setError('Failed to load workflow');
      console.error('Error loading workflow:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if source node has existing outgoing connections
  const hasExistingSourceConnection = useCallback((sourceNodeId: string) => {
    return edges.some((edge: any) => edge.source === sourceNodeId);
  }, [edges]);

  // Check if supervisor's output handle has existing connections
  const hasExistingOutputConnection = useCallback((sourceNodeId: string) => {
    return edges.some((edge: any) => edge.source === sourceNodeId && edge.sourceHandle === 'output');
  }, [edges]);

  // Check if conditional route's true handle has existing connections
  const hasExistingTrueConnection = useCallback((sourceNodeId: string) => {
    return edges.some((edge: any) => edge.source === sourceNodeId && edge.sourceHandle === 'true');
  }, [edges]);

  // Check if conditional route's false handle has existing connections
  const hasExistingFalseConnection = useCallback((sourceNodeId: string) => {
    return edges.some((edge: any) => edge.source === sourceNodeId && edge.sourceHandle === 'false');
  }, [edges]);

  // Connection validation function
  const isValidConnection = useCallback((connection: Connection) => {
    const { source, sourceHandle } = connection;

    // Prevent self-connections
    if (!source || source === connection.target) return false;

    // Get source node to check its type
    const sourceNode = nodes.find(n => n.id === source);

    // Restrict user input nodes to only one outgoing connection
    if (sourceNode?.type === 'userInput' && hasExistingSourceConnection(source)) {
      console.log('User input node already has an outgoing connection. Connection rejected.');
      return false;
    }

    // Restrict supervisor's output handle to only one connection
    if (sourceNode?.type === 'supervisor' && sourceHandle === 'output' && hasExistingOutputConnection(source)) {
      console.log('Supervisor output handle already has a connection. Connection rejected.');
      return false;
    }

    // Restrict conditional route's true handle to only one connection
    if (sourceNode?.type === 'conditionalRoute' && sourceHandle === 'true' && hasExistingTrueConnection(source)) {
      console.log('Conditional route true handle already has a connection. Connection rejected.');
      return false;
    }

    // Restrict conditional route's false handle to only one connection
    if (sourceNode?.type === 'conditionalRoute' && sourceHandle === 'false' && hasExistingFalseConnection(source)) {
      console.log('Conditional route false handle already has a connection. Connection rejected.');
      return false;
    }

    return true;
  }, [nodes, hasExistingSourceConnection, hasExistingOutputConnection, hasExistingTrueConnection, hasExistingFalseConnection]);

  const onConnect = useCallback(
    (params: Connection) => {
      console.log('Connection params:', params); // Debug log

      // Validate the connection before proceeding
      if (!isValidConnection(params)) {
        return; // Reject the connection
      }

      // Handle false connections from conditional route nodes
      if (params.sourceHandle === 'false') {
        const sourceNode = nodes.find(n => n.id === params.source);
        if (sourceNode?.type === 'conditionalRoute') {
          console.log('Adding false connection from conditional route:', params.source, '->', params.target);

          // Update node data to store the connection
          setNodes((nds) => nds.map((node) => {
            if (node.id === params.source) {
              const currentFalseConnections = node.data.falseConnections || [];
              if (!currentFalseConnections.includes(params.target)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    falseConnections: [...currentFalseConnections, params.target],
                  },
                };
              }
            }
            return node;
          }));

          // Create a visual edge for the connection
          const visualEdge = {
            id: `false-link-${params.source}-${params.target}`,
            source: params.source!,
            target: params.target!,
            sourceHandle: 'false',
            targetHandle: undefined,
            type: 'animatedDashed',
            animated: true,
            style: { stroke: '#666' },
            data: { connectionType: 'false' },
          };

          setEdges((eds) => addEdge(visualEdge, eds));
          return; // Don't process as regular edge
        }
      }

      // Determine the correct source handle for supervisor connections
      let sourceHandle = params.sourceHandle;

      // If connecting from supervisor to output node, use the 'output' handle
      if (params.sourceHandle === 'agents' && params.target) {
        const sourceNode = nodes.find(n => n.id === params.source);
        const targetNode = nodes.find(n => n.id === params.target);

        if (sourceNode?.type === 'supervisor' && targetNode?.type === 'output') {
          sourceHandle = 'output';
          console.log('Auto-routing supervisor to output via output handle');
        }
      }

      const connectionParams = {
        ...params,
        sourceHandle,
        type: 'animatedDashed',
        animated: true,
        style: { stroke: '#666' },
      };

      setEdges((eds) => addEdge(connectionParams, eds));

      // If connecting from supervisor's "agents" handle, hide agent's output handle
      if (sourceHandle === 'agents') {
        console.log('Hiding output handle for agent:', params.target); // Debug log
        setNodes((nds) => nds.map((node) => {
          if (node.id === params.target && node.type === 'agent') {
            console.log('Updating node:', node.id, 'to hide output handle'); // Debug log
            return {
              ...node,
              data: {
                ...node.data,
                hideOutputHandle: true,
              },
            };
          }
          return node;
        }));
      }
    },
    [setEdges, setNodes, nodes, isValidConnection]
  );

  const handleSave = () => {
    if (isNewWorkflow) {
      setShowSaveDialog(true);
    } else {
      saveWorkflow();
    }
  };

  const saveWorkflow = async () => {
    try {
      dispatch(StartLoading());
      setError(null);

      const { nodes: apiNodes, edges: apiEdges } = WorkflowService.convertToApiFormat(nodes, edges);

      const workflowData: SaveWorkflowRequest = {
        name: workflowName,
        description: workflowDescription,
        nodes: apiNodes,
        edges: apiEdges,
        variables: []
      };

      if (isNewWorkflow) {
        const savedWorkflow = await WorkflowService.saveWorkflow(workflowData);
        console.log('saveWorkflow - Saved Data:', savedWorkflow);
        setShowSaveDialog(false);
        setIsNewWorkflow(false);
        // Update URL without reloading the page
        navigate(`/workflows/edit/${savedWorkflow.id}`, { replace: true });
      } else {
        await WorkflowService.updateWorkflowData(workflowId!, workflowData);
      }
    } catch (err) {
      dispatch(StopLoading());
      var errorMessage = axios.isAxiosError(err) && err.response?.data?.detail
        ? err.response.data.detail
        : 'Failed to save workflow';
      dispatch(ShowError(errorMessage));
      console.error('Error saving workflow:', err);
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleRun = async () => {
    if (!workflowId) {
      console.error('No workflow ID available');
      return;
    }

    // Open chat panel instead of executing immediately
    setShowChatPanel(true);
    setShowConfigPanel(false);

    // Add welcome message
    if (chatMessages.length === 0) {
      setChatMessages([{
        sender: PlayGroundEnum.AGENT,
        agentData: {
          response: 'Hello! I\'m ready to help you test this workflow. What would you like to ask?',
          rawResponse: 'Hello! I\'m ready to help you test this workflow. What would you like to ask?',
          isWelcomeMessage: true,
        }
      }]);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || !workflowId || isExecuting) return;

    const userMessage: Message = {
      sender: PlayGroundEnum.USER,
      userData: {
        input: chatInput.trim(),
        conversation_id: workflowId
      }
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsExecuting(true);


    try {
      const response = await BaseInstance.post(`/workflows/nodes/${workflowId}/execute`, {
        input: chatInput.trim()
      });

      console.log('📥 Workflow response:', response.data);

      // Handle standardized output format
      const output = response.data.output || {};
      const isFile = output.type === 'file';
      const rawResponse = output.content || output || '';

      // Try to get response_id from multiple possible locations
      const responseId = response.data.response_id || output?.response_id || '';
      const conversation_id = response.data.conversation_id || output?.conversation_id || '';

      console.log('📋 Response ID extracted:', responseId);
      console.log('📋 Conversation ID extracted:', conversation_id);
      console.log('📦 Output object:', output);
      console.log('📦 Full response.data:', response.data);

      // Update the workflow conversation ID if we got one
      if (conversation_id) {
        setWorkflowConversationId(conversation_id);
      }

      const assistantMessage: Message = {
        sender: PlayGroundEnum.AGENT,
        agentData: {
          response: isFile ? output.content : rawResponse,
          rawResponse: isFile ? output.content : rawResponse,
          conversation_id: conversation_id,
          response_id: responseId,
          is_complete: true,
          isFile: isFile,
          fileName: isFile ? output.filename || 'download.pdf' : undefined
        }
      };
      setChatMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error executing workflow:', error);
      const errorMessage: Message = {
        sender: PlayGroundEnum.AGENT,
        agentData: {
          response: 'Sorry, there was an error executing the workflow. Please try again.',
          rawResponse: 'Sorry, there was an error executing the workflow. Please try again.',
          is_complete: true
        }
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleClose = () => {
    navigate('/workflows');
  };

  const handleNodeClick = (event: React.MouseEvent, node: Node) => {
    if (node.type === 'supervisor') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'agent') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'knowledgeBase') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'dataParser') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'conditionalRoute') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'pdfGenerator') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    } else if (node.type === 'emailSender') {
      setSelectedNode(node);
      setShowConfigPanel(true);
    }
  };

  const handleConfigSave = (nodeId: string, data: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...data } }
          : node
      )
    );
    setShowConfigPanel(false);
    setSelectedNode(null);
  };

  const handleConfigClose = () => {
    setShowConfigPanel(false);
    setSelectedNode(null);
  };

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();

    const nodeType = event.dataTransfer.getData('application/reactflow');

    if (typeof nodeType === 'undefined' || !nodeType || !reactFlowInstance) {
      return;
    }

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    let newNode: Node;

    switch (nodeType) {
      case 'userInput':
        newNode = {
          id: `userInput-${uuidv4()}`,
          type: 'userInput',
          position,
          data: { label: 'User Input', inputType: 'Input Message' },
        };
        break;
      case 'supervisor':
        newNode = {
          id: `supervisor-${uuidv4()}`,
          type: 'supervisor',
          position,
          data: { label: 'Supervisor' },
        };
        break;
      case 'agent':
        newNode = {
          id: `agent-${uuidv4()}`,
          type: 'agent',
          position,
          data: { label: 'Agent', agentType: 'gpt-4o-mini' },
        };
        break;
      case 'knowledgeBase':
        newNode = {
          id: `knowledgeBase-${uuidv4()}`,
          type: 'knowledgeBase',
          position,
          data: { label: 'Knowledge Base', kbType: 'gpt-4o-mini' },
        };
        break;
      case 'dataParser':
        newNode = {
          id: `dataParser-${uuidv4()}`,
          type: 'dataParser',
          position,
          data: { label: 'Data Parser', parserType: 'gpt-4o-mini' },
        };
        break;
      case 'output':
        newNode = {
          id: `output-${uuidv4()}`,
          type: 'output',
          position,
          data: { label: 'Output', outputType: 'text' },
        };
        break;
      case 'conditionalRoute':
        newNode = {
          id: `conditionalRoute-${uuidv4()}`,
          type: 'conditionalRoute',
          position,
          data: { label: 'Conditional Route', condition: '' },
        };
        break;
      case 'pdfGenerator':
        newNode = {
          id: `pdfGenerator-${uuidv4()}`,
          type: 'pdfGenerator',
          position,
          data: { label: 'PDF Generator', filename: 'output.pdf', title: 'Generated Document', enableMarkdown: true },
        };
        break;
      case 'emailSender':
        newNode = {
          id: `emailSender-${uuidv4()}`,
          type: 'emailSender',
          position,
          data: { label: 'Email Sender', toEmail: '', subject: 'Workflow Notification' },
        };
        break;
      default:
        return;
    }

    setNodes((nds) => [...nds, newNode]);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <AppBar position="relative" sx={{ backgroundColor: '#1976d2', zIndex: 1000, flexShrink: 0 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#ffffff' }}>
            Workflow Builder
          </Typography>
          {/* Run button restored */}
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleRun}
            disabled={isLoading}
            sx={{
              mr: 1,
              backgroundColor: '#4caf50',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {isLoading ? 'Running...' : 'Run Workflow'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isLoading}
            sx={{
              mr: 1,
              backgroundColor: '#1e88e5',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {isLoading ? 'Saving...' : 'Save Workflow'}
          </Button>
          <IconButton color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Sidebar */}
        <Paper
          sx={{
            width: 300,
            p: 2,
            borderRadius: 0,
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Workflow Details
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Name
            </Typography>
            <input
              type="text"
              value={workflowName}
              onChange={(e) => {
                if (e.target.value.length <= 40) {
                  setWorkflowName(e.target.value)
                }
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <textarea
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Node Palette
          </Typography>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* User Input Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'userInput')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  User Input
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  User data input
                </Typography>
              </Box>

              {/* Supervisor Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'supervisor')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Supervisor
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Workflow supervisor
                </Typography>
              </Box>

              {/* Agent Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'agent')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Agent
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  AI agent processor
                </Typography>
              </Box>

              {/* Knowledge Base Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'knowledgeBase')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Knowledge Base
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Knowledge repository
                </Typography>
              </Box>

              {/* Data Parser Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'dataParser')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Data Parser
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Data processing
                </Typography>
              </Box>

              {/* Output Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'output')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Output
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Final output
                </Typography>
              </Box>

              {/* Conditional Route Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'conditionalRoute')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Conditional Route
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Route based on condition
                </Typography>
              </Box>

              {/* PDF Generator Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'pdfGenerator')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  PDF Generator
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Generate PDF files
                </Typography>
              </Box>

              {/* Email Sender Node */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'grab',
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                draggable
                onDragStart={(event) => onDragStart(event, 'emailSender')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Email Sender
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Send email notifications
                </Typography>
              </Box>

            </Box>
          </Box>
        </Paper>

        {/* React Flow Canvas */}
        <Box sx={{ flex: 1, height: '100%', overflow: 'hidden' }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={handleNodeClick}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
          >
            <Controls />
            <MiniMap />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </Box>

        {/* Configuration Panel */}
        {showConfigPanel && selectedNode && (
          <>
            {selectedNode.type === 'supervisor' && (
              <SupervisorConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'agent' && (
              <AgentConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'knowledgeBase' && (
              <KnowledgeBaseConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'dataParser' && (
              <DataParserConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'conditionalRoute' && (
              <ConditionalRouteConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'pdfGenerator' && (
              <PDFGeneratorConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
            {selectedNode.type === 'emailSender' && (
              <EmailSenderConfigPanel
                key={selectedNode.id}
                nodeId={selectedNode.id}
                nodeData={selectedNode.data}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
              />
            )}
          </>
        )}

        {/* Chat Panel */}
        {showChatPanel && (
          <Paper
            sx={{
              width: 400,
              height: '100%',
              borderRadius: 0,
              borderLeft: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">Workflow Chat</Typography>
              <IconButton onClick={() => setShowChatPanel(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Chat Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 1,
              }}
            >
              <List>
                {chatMessages.map((message, index) => (
                  <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        width: '100%',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          bgcolor: message.sender === PlayGroundEnum.USER ? '#1976d2' : '#4caf50',
                        }}
                      >
                        {message.sender === PlayGroundEnum.USER ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                      </Avatar>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {message.sender === PlayGroundEnum.USER ? 'You' : 'Workflow'}
                      </Typography>
                    </Box>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: message.sender === PlayGroundEnum.USER ? '#e3f2fd' : '#f1f8e9',
                        borderRadius: 2,
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.sender === PlayGroundEnum.USER ? (
                        <Typography variant="body2">
                          {message.userData?.input || ''}
                        </Typography>
                      ) : message.agentData?.isFile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Your file is ready for download
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              if (message.agentData?.rawResponse) {
                                try {
                                  // Decode base64 content
                                  const base64Content = message.agentData.rawResponse;
                                  const binaryString = window.atob(base64Content);
                                  const bytes = new Uint8Array(binaryString.length);
                                  for (let i = 0; i < binaryString.length; i++) {
                                    bytes[i] = binaryString.charCodeAt(i);
                                  }

                                  // Create blob and download
                                  const blob = new Blob([bytes], { type: 'application/pdf' });
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = message.agentData.fileName || 'download.pdf';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error downloading PDF:', error);
                                }
                              }
                            }}
                            sx={{
                              backgroundColor: '#d32f2f',
                              color: '#ffffff',
                              '&:hover': {
                                backgroundColor: '#b71c1c',
                              },
                              textTransform: 'none',
                            }}
                          >
                            Download PDF
                          </Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          component="div"
                          dangerouslySetInnerHTML={{
                            __html: parseMarkdown(message.agentData?.response || '')
                          }}
                        />
                      )}
                    </Paper>
                    {/* Add AgentPrompts for assistant messages */}
                    {message.sender === PlayGroundEnum.AGENT && (
                      <AgentPrompts
                        agentShortuts={{}}
                        messageId={index}
                        sendMessageShortcuts={() => { }}
                        messages={chatMessages}
                        setMessages={setChatMessages}
                        agentId={workflowId}
                        conversationId={workflowConversationId}
                        responseId={message.agentData?.response_id}
                        hideRewrite={true}
                        hideAllButtons={message.agentData?.isWelcomeMessage || false}
                      />
                    )}
                  </ListItem>
                ))}
                {isExecuting && (
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Executing workflow...
                    </Typography>
                  </ListItem>
                )}
                <div ref={chatEndRef} />
              </List>
            </Box>

            {/* Chat Input */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSubmit();
                  }
                }}
                disabled={isExecuting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleChatSubmit}
                        disabled={!chatInput.trim() || isExecuting}
                        color="primary"
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ position: 'fixed', top: 80, right: 20, zIndex: 1000 }}
          >
            {error}
          </Alert>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Save Workflow</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Workflow Name"
              fullWidth
              variant="outlined"
              value={workflowName}
              onChange={(e) => {
                if (e.target.value.length <= 40) {
                  setWorkflowName(e.target.value)
                }
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button
              onClick={saveWorkflow}
              variant="contained"
              disabled={!workflowName.trim() || isLoading}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default WorkflowBuilder;

